import { supabase } from "./supabase";

const TOKEN_KEY = "campo_ia_token";
const USER_KEY = "campo_ia_user";

// Helper: bcrypt no está disponible en frontend, usar endpoint de login del backend
// Por ahora usamos una función hash simple (NO PRODUCTION — solo para MVP)
// En producción: usar Web Crypto API o llamar a un backend que haga bcrypt

async function hashPassword(password) {
  // Temporal: encoder simple. En producción usar bcrypt via servidor
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function comparePasswords(password, hash) {
  const computed = await hashPassword(password);
  return computed === hash;
}

export const authService = {
  async signup(email, password, nombreCampo) {
    if (!email || !password) throw new Error("Email y contraseña requeridos");

    const passwordHash = await hashPassword(password);
    const userId = crypto.randomUUID(); // Generar UUID para id

    const { data, error } = await supabase
      .from("usuarios")
      .insert([{
        id: userId,
        email,
        password_hash: passwordHash,
        nombre_campo: nombreCampo
      }])
      .select()
      .single();

    if (error) throw new Error(error.message || "Error al registrar");

    // Login automático después del signup
    return authService.crearToken(data);
  },

  async login(email, password) {
    if (!email || !password) throw new Error("Email y contraseña requeridos");

    const { data, error } = await supabase
      .from("usuarios")
      .select("*")
      .eq("email", email)
      .single();

    if (error || !data) throw new Error("Usuario no encontrado");
    if (!data.activo) throw new Error("Usuario desactivado");

    const validPassword = await comparePasswords(password, data.password_hash);
    if (!validPassword) throw new Error("Contraseña incorrecta");

    return authService.crearToken(data);
  },

  crearToken(usuario) {
    // JWT simplificado (sin firma — solo para MVP)
    const payload = {
      id: usuario.id,
      email: usuario.email,
      nombre_campo: usuario.nombre_campo,
      rol: usuario.rol,
      features: usuario.features || {},
      iat: Date.now(),
      exp: Date.now() + 7 * 24 * 60 * 60 * 1000, // 7 días
    };

    const token = btoa(JSON.stringify(payload)); // Base64 encoding
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(payload));

    return { token, usuario: payload };
  },

  obtenerToken() {
    return localStorage.getItem(TOKEN_KEY);
  },

  obtenerUsuario() {
    const user = localStorage.getItem(USER_KEY);
    if (!user) return null;

    try {
      const payload = JSON.parse(user);
      // Verificar expiración
      if (payload.exp < Date.now()) {
        authService.logout();
        return null;
      }
      return payload;
    } catch (e) {
      authService.logout();
      return null;
    }
  },

  logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  async actualizar(updates) {
    const usuario = authService.obtenerUsuario();
    if (!usuario) throw new Error("No autenticado");

    const { data, error } = await supabase
      .from("usuarios")
      .update(updates)
      .eq("id", usuario.id)
      .select()
      .single();

    if (error) throw new Error(error.message);

    // Actualizar token si cambió rol o features
    if (updates.rol || updates.features || updates.nombre_campo) {
      return authService.crearToken(data);
    }

    return data;
  },

  async listarUsuarios() {
    const usuario = authService.obtenerUsuario();
    if (usuario?.rol !== "superadmin") throw new Error("No autorizado");

    const { data, error } = await supabase
      .from("usuarios")
      .select("id, email, nombre_campo, rol, features, activo, created_at");

    if (error) throw new Error(error.message);
    return data || [];
  },

  async actualizarUsuario(userId, updates) {
    const usuario = authService.obtenerUsuario();
    if (usuario?.rol !== "superadmin") throw new Error("No autorizado");

    const { data, error } = await supabase
      .from("usuarios")
      .update(updates)
      .eq("id", userId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  },

  async crearUsuario(email, password, nombreCampo, rol = "user") {
    const usuario = authService.obtenerUsuario();
    if (usuario?.rol !== "superadmin") throw new Error("No autorizado");

    const passwordHash = await hashPassword(password);
    const { data, error } = await supabase
      .from("usuarios")
      .insert([{ email, password_hash: passwordHash, nombre_campo: nombreCampo, rol }])
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  },

  async agregarAccesoCampo(usuarioPropietarioId, usuarioAccesoId, permiso = "lectura") {
    const usuario = authService.obtenerUsuario();
    if (usuario?.id !== usuarioPropietarioId && usuario?.rol !== "superadmin")
      throw new Error("No autorizado");

    const { data, error } = await supabase
      .from("permisos_campos")
      .insert([{ usuario_propietario_id: usuarioPropietarioId, usuario_acceso_id: usuarioAccesoId, permiso }])
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  },

  async obtenerAccesoCampos(usuarioId) {
    const usuario = authService.obtenerUsuario();
    if (!usuario) throw new Error("No autenticado");

    const { data, error } = await supabase
      .from("permisos_campos")
      .select("*")
      .or(`usuario_propietario_id.eq.${usuarioId},usuario_acceso_id.eq.${usuarioId}`);

    if (error) throw new Error(error.message);
    return data || [];
  },

  async eliminarAccesoCampo(usuarioPropietarioId, usuarioAccesoId) {
    const usuario = authService.obtenerUsuario();
    if (usuario?.id !== usuarioPropietarioId && usuario?.rol !== "superadmin")
      throw new Error("No autorizado");

    const { error } = await supabase
      .from("permisos_campos")
      .delete()
      .match({ usuario_propietario_id: usuarioPropietarioId, usuario_acceso_id: usuarioAccesoId });

    if (error) throw new Error(error.message);
  },
};
